// StapleStar — per-canonical "this is a staple I always want stocked"
// toggle. Renders as a 32x32 star button. Filled gold when the
// canonical id is in the user's profile.staple_canonicals array,
// hollow muted when not. Tap toggles via useProfile.upsert.
//
// Wired only when the item has a resolved canonical id — pre-
// canonical (free-text) rows hide the star. Once the user picks a
// canonical, the star surfaces.
//
// First-time coach mark: a one-shot tooltip points at the star the
// FIRST time a user opens any item card. localStorage flag
// `staples-coachmark.seen.v1:${userId}` ensures it only fires once
// per user. Tap-to-dismiss; auto-dismisses on first toggle.
//
// Optimistic update with rollback on error matches the
// useProfile.upsert pattern used elsewhere (avatar, dietary, etc.).

import { useEffect, useMemo, useRef, useState } from "react";
import { COLOR, FONT } from "../lib/tokens";
import { useProfile } from "../lib/useProfile";

const COACHMARK_KEY = (uid) => `staples-coachmark.seen.v1:${uid || "anon"}`;

function readCoachmarkSeen(userId) {
  try {
    if (typeof localStorage === "undefined") return true;
    return localStorage.getItem(COACHMARK_KEY(userId)) === "1";
  } catch { return true; }
}
function writeCoachmarkSeen(userId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(COACHMARK_KEY(userId), "1");
  } catch { /* ignore */ }
}

export default function StapleStar({ canonicalId, userId }) {
  const { profile, upsert } = useProfile(userId);
  const stapleIds = useMemo(
    () => Array.isArray(profile?.staple_canonicals) ? profile.staple_canonicals : [],
    [profile?.staple_canonicals],
  );
  const isStaple = stapleIds.includes(canonicalId);

  // Coach mark — fire on FIRST mount of the star for this user, IF
  // they've never seen it. Wraps in a small delay so the modal sheet
  // animation completes before the tooltip lands.
  const [showCoach, setShowCoach] = useState(false);
  const dismissedRef = useRef(false);
  useEffect(() => {
    if (!canonicalId || !userId) return undefined;
    if (readCoachmarkSeen(userId)) return undefined;
    const t = setTimeout(() => {
      if (!dismissedRef.current) setShowCoach(true);
    }, 450);
    return () => clearTimeout(t);
  }, [canonicalId, userId]);
  const dismissCoach = () => {
    dismissedRef.current = true;
    setShowCoach(false);
    writeCoachmarkSeen(userId);
  };

  if (!canonicalId) return null;

  const toggle = (e) => {
    e.stopPropagation();
    if (showCoach) dismissCoach();
    const next = isStaple
      ? stapleIds.filter((id) => id !== canonicalId)
      : [...stapleIds, canonicalId];
    upsert?.({ staple_canonicals: next }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[StapleStar] toggle failed:", err?.message || err);
    });
  };

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={isStaple}
        aria-label={isStaple ? "Unmark as staple" : "Mark as staple"}
        title={isStaple ? "This is one of your staples" : "Mark as staple"}
        style={{
          width: 32, height: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: isStaple ? "rgba(212,166,55,0.16)" : "transparent",
          border: `1px solid ${isStaple ? "rgba(212,166,55,0.55)" : "rgba(30,30,30,0.18)"}`,
          borderRadius: 999,
          color: isStaple ? COLOR.gold : COLOR.muted,
          fontSize: 16,
          lineHeight: 1,
          cursor: "pointer",
          transition: "background 0.18s ease, border-color 0.18s ease, color 0.18s ease",
          flexShrink: 0,
        }}
      >
        {isStaple ? "★" : "☆"}
      </button>

      {showCoach && (
        <div
          onClick={dismissCoach}
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            zIndex: 50,
            width: 240,
            padding: "10px 12px 12px",
            background: COLOR.night,
            border: `1px solid ${COLOR.border}`,
            borderRadius: 12,
            boxShadow: "0 12px 28px rgba(30,20,8,0.18), 0 2px 6px rgba(30,20,8,0.08)",
            cursor: "pointer",
          }}
        >
          {/* tooltip arrow pointing at the star */}
          <div style={{
            position: "absolute",
            top: -6,
            right: 10,
            width: 12,
            height: 12,
            background: COLOR.night,
            borderTop: `1px solid ${COLOR.border}`,
            borderLeft: `1px solid ${COLOR.border}`,
            transform: "rotate(45deg)",
          }} />
          <div style={{
            fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
            color: COLOR.gold, letterSpacing: "0.16em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}>
            New
          </div>
          <div style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic", fontWeight: 500,
            fontSize: 15, lineHeight: 1.25,
            color: COLOR.ink, marginBottom: 6,
          }}>
            Mark items as staples
          </div>
          <div style={{
            fontFamily: FONT.forma, fontSize: 12,
            color: COLOR.muted, lineHeight: 1.45,
          }}>
            Tap the star on any item to flag it. We'll let you know
            when one runs low — right on the home page.
          </div>
        </div>
      )}
    </div>
  );
}
